import {
  Badge,
  type BadgeProps,
  Card,
  type CardProps,
  Checkbox,
  ContextMenuDropdown,
  SkeletonLoader,
  type TDropdownItem,
  Tooltip,
} from '@payaca/ambrosia';
import { useHover } from '@payaca/hooks';
import UntitledIcon, { TIconName } from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import React, { FC, forwardRef } from 'react';

export type TProps = {
  folder?: string;
  subFolder?: boolean;
  fileName: string;
  fileType: string;
  formattedSize?: string;
  formattedDate?: string;
  hovered?: boolean;
  selected?: boolean;
  contextMenuItems?: TDropdownItem[];
  thumbnailUrl?: string | null;
  thumbnailIconName?: TIconName;
  badge?: BadgeProps & {
    text: string;
  };
  onSelectChange?: (selected: boolean) => void;
  checkbox?: boolean;
  sizeVariant?: 'xs' | 'sm' | 'md' | 'lg';
} & CardProps<'button'>;

const FolderEntry = forwardRef<HTMLButtonElement, TProps>((props, ref) => {
  const {
    className,
    folder,
    subFolder,
    fileName,
    sizeVariant = 'sm',
    fileType,
    formattedSize,
    formattedDate,
    thumbnailUrl,
    thumbnailIconName,
    hovered = false,
    selected = false,
    contextMenuItems = [],
    badge,
    checkbox = true,
    onSelectChange,
    onClick,
    ...rest
  } = props;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (event.metaKey || event.ctrlKey) {
      onSelectChange?.(!selected);
    } else {
      onClick?.(event);
    }
  };

  return useHover((isHovered) => (
    <Card
      as="button"
      className={clstx(
        'p-0 text-left group w-full overflow-hidden data-[selected]:ring-2 data-[selected]:ring-blue-900 cursor-pointer aspect-square',
        className
      )}
      // when the file is selected, hover effects no longer apply
      data-hovered={selected ? undefined : hovered || isHovered || undefined}
      data-selected={selected || undefined}
      sizeVariant={sizeVariant}
      ref={ref}
      onClick={handleClick}
      {...rest}
    >
      <FolderEntryThumbnail
        fileName={fileName}
        thumbnailUrl={thumbnailUrl}
        thumbnailIconName={thumbnailIconName}
        badge={badge}
        selected={selected}
        onSelectChange={onSelectChange}
        checkbox={checkbox}
      />

      <Tooltip
        tooltipContent={fileName}
        placement="bottom"
        showArrow
        className="w-full"
      >
        <Card.Body className="group-data-[hovered]:bg-gray-100 group-data-[selected]:bg-gray-100">
          {folder && (
            <div className="supporting-body text-gray-500 inline-flex items-center gap-1.5 w-full">
              <UntitledIcon className="h-4 w-4 shrink-0" name="folder.3" />
              {subFolder && (
                <>
                  ...
                  <UntitledIcon
                    className="h-4 w-4 shrink-0"
                    name="chevron-right"
                  />
                </>
              )}
              <p className="truncate supporting-body">{folder}</p>
            </div>
          )}
          <p className="supporting-body truncate min-w-0">{fileName}</p>

          <div className="flex justify-between">
            <p className="supporting-body text-gray-500 capitalize">
              {fileType}
              {formattedSize && ' - ' + formattedSize}
              {formattedDate && ' - ' + formattedDate}
            </p>

            {contextMenuItems && contextMenuItems.length > 0 && (
              <ContextMenuDropdown
                className="-m-2"
                menuItems={contextMenuItems}
                variant="quaternary"
                size="xs"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              />
            )}
          </div>
        </Card.Body>
      </Tooltip>
    </Card>
  ));
});

type TFolderEntryThumbnailProps = Pick<
  TProps,
  | 'fileName'
  | 'thumbnailUrl'
  | 'thumbnailIconName'
  | 'badge'
  | 'selected'
  | 'onSelectChange'
  | 'checkbox'
>;

const FolderEntryThumbnail: FC<TFolderEntryThumbnailProps> = (props) => {
  const {
    fileName,
    thumbnailUrl,
    thumbnailIconName,
    badge,
    selected,
    onSelectChange,
    checkbox,
  } = props;

  return (
    <div className="relative w-full flex flex-col items-center flex-1 bg-gray-100 overflow-hidden">
      {thumbnailUrl ? (
        <img
          className="absolute inset-0 w-full h-full object-cover transition-transform group-data-[hovered]:scale-110"
          src={thumbnailUrl}
          alt={fileName + ' Thumbnail'}
        />
      ) : (
        <UntitledIcon
          className="h-4/6 w-4/6 transition-transform group-data-[hovered]:scale-110 m-auto"
          name={thumbnailIconName || 'file-04'}
        />
      )}

      {checkbox && (
        <Checkbox
          className="[@media(hover:hover)]:opacity-0 group-data-[hovered]:opacity-100 group-data-[selected]:opacity-100 focus:opacity-100 absolute left-3 top-3"
          checked={selected}
          onClick={(event) => {
            event.stopPropagation();
          }}
          onChange={(event) => {
            event.stopPropagation();
            onSelectChange?.(event.target.checked);
          }}
        />
      )}

      {badge && (
        <Badge
          size="sm"
          {...badge}
          className={clstx('absolute top-3 right-3', badge.className)}
        >
          {badge.text}
        </Badge>
      )}
    </div>
  );
};

const SkeletonLoaderFolderEntry: FC<{ delayClassName?: string }> = (props) => {
  const { delayClassName } = props;

  return (
    <Card sizeVariant="sm" className="aspect-square overflow-hidden">
      <SkeletonLoader.Square
        className={clstx('w-full h-3/5', delayClassName)}
      />
      <Card.Body className="grow relative">
        <SkeletonLoader.Text className={delayClassName} />
      </Card.Body>
    </Card>
  );
};

export default Object.assign(FolderEntry, {
  Loading: SkeletonLoaderFolderEntry,
});
